import { useEffect, useRef, useState } from 'react'
import { Check, Fingerprint } from 'lucide-react'
import { isWebAuthnSupported, registerPasskey } from '@/api/webauthn'
import { useAuthStore } from '@/stores/auth'
import {
  MAX_ASKS,
  asksSoFar,
  isSharedComputer,
  markSharedComputer,
  recordAsk,
  signalledThisPageLoad,
  silenceForUser,
} from './passkey-offer'

const SUCCESS_HOLD_MS = 900

/**
 * Post-sign-in passkey offer (CLA-893).
 *
 * Enrolment used to be offered once, in the onboarding wizard, per account.
 * But a passkey is bound to ONE authenticator, so coverage decays silently:
 * every new laptop and replaced phone drops the user back to the slowest
 * sign-in path with nothing ever offering again except Settings, which nobody
 * opens. This closes that by asking on any device that lacks one.
 *
 * The trigger is a one-shot cookie the backend sets on every web sign-in that
 * did NOT use a passkey. That is a per-BROWSER signal, which is the whole
 * point — an account-wide "does this user have any passkeys?" query cannot see
 * device turnover, and is exactly why the iOS prompt never fires on a
 * replacement phone. "Signed in without a passkey here" cannot have that bug:
 * had a usable passkey existed on this device, it would have been used.
 *
 * Renders children untouched in every other case. This is an offer, not a
 * gate — but it does take the screen, because an in-app card gets ignored
 * indefinitely, and immediately after sign-in is both the moment the user just
 * felt the friction and the correct security moment to enrol a new
 * authenticator (identity was just proven). Dismissal is therefore one tap: a
 * clinician opening Claro between patients must never be held up.
 */
export function PasskeyOfferGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const [dismissed, setDismissed] = useState(false)
  const recorded = useRef(false)

  // Derived, not stored — so nothing has to be reconciled in an effect. The
  // cookie read is memoised for the page load (see `signalledThisPageLoad`),
  // which is what makes it safe to call during render.
  const offering =
    !dismissed &&
    !!user &&
    signalledThisPageLoad() &&
    isWebAuthnSupported() &&
    !isSharedComputer() &&
    asksSoFar(user.id) < MAX_ASKS

  // Count the ask exactly once. The ref guard matters: StrictMode runs effects
  // twice in development, and without it a single offer would burn both of the
  // user's two lifetime asks.
  useEffect(() => {
    if (offering && user && !recorded.current) {
      recorded.current = true
      recordAsk(user.id)
    }
  }, [offering, user])

  if (!offering || !user) return <>{children}</>

  return <PasskeyOfferScreen userId={user.id} onDone={() => setDismissed(true)} />
}

function PasskeyOfferScreen({ userId, onDone }: { userId: number; onDone: () => void }) {
  const [enrolling, setEnrolling] = useState(false)
  const [success, setSuccess] = useState(false)
  const [cancelledHint, setCancelledHint] = useState(false)
  const [error, setError] = useState('')

  // Hold the success state briefly before dismissing. Silent navigation reads
  // exactly like a skip, and onboarding users were ending up without a passkey
  // because they never realised registration had succeeded (PasskeyStep).
  useEffect(() => {
    if (!success) return
    const t = setTimeout(onDone, SUCCESS_HOLD_MS)
    return () => clearTimeout(t)
  }, [success, onDone])

  async function handleEnrol() {
    setError('')
    setCancelledHint(false)
    setEnrolling(true)
    try {
      await registerPasskey('/api/v1/auth/webauthn', 'This device')
      silenceForUser(userId)
      setSuccess(true)
    } catch (err) {
      if (err instanceof Error) {
        // The user dismissed the platform's biometric prompt. Treating that as
        // an opt-out silently drops people into a no-passkey hole, so offer a
        // retry rather than closing (matches PasskeyStep).
        if (err.name === 'NotAllowedError') {
          setCancelledHint(true)
          return
        }
        // The authenticator already holds a credential for this account on
        // this device — excludeCredentials did its job. Nothing to offer, and
        // nothing to fix; stop asking.
        if (err.name === 'InvalidStateError') {
          silenceForUser(userId)
          setSuccess(true)
          return
        }
        setError(err.message)
        return
      }
      setError('Passkey setup failed')
    } finally {
      setEnrolling(false)
    }
  }

  function handleNotNow() {
    onDone()
  }

  function handleSharedComputer() {
    markSharedComputer()
    onDone()
  }

  return (
    <div className="flex flex-col items-center justify-center h-dvh w-full bg-surface px-6">
      <div className="w-full max-w-sm rounded-xl border border-border-subtle bg-white px-6 py-8">
        <div className="flex flex-col items-center gap-5">
          <div
            className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${
              success ? 'bg-success-light border-success/25' : 'bg-claro-50 border-claro-200'
            }`}
          >
            {success ? (
              <Check className="w-10 h-10 text-success" />
            ) : (
              <Fingerprint className="w-10 h-10 text-claro-600" />
            )}
          </div>

          <div className="text-center">
            <h1 className="text-lg font-semibold text-ink">
              {success ? 'Saved.' : 'Sign in faster next time'}
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              {success
                ? 'Next time you can sign in with your fingerprint or face — no email link, no password.'
                : 'Use your fingerprint, face, or device PIN instead of a password or an emailed link. The passkey stays on this device — we never see it.'}
            </p>
          </div>
        </div>

        {cancelledHint && (
          <div className="mt-5 bg-warning-light text-on-warning-soft rounded-lg p-3 text-sm">
            Your device closed the prompt before the passkey was saved. Try again, or add one
            later from Settings → Security.
          </div>
        )}

        {error && (
          <div className="mt-5 bg-error-light text-on-error-soft rounded-lg p-3 text-sm">{error}</div>
        )}

        {!success && (
          <div className="mt-6 space-y-3">
            <button
              onClick={handleEnrol}
              disabled={enrolling}
              className="w-full py-3 px-4 bg-claro-600 text-white font-medium rounded-lg hover:bg-claro-700 transition-colors disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed"
            >
              {enrolling ? 'Setting up…' : cancelledHint ? 'Try again' : 'Set up'}
            </button>
            <button
              onClick={handleNotNow}
              disabled={enrolling}
              className="w-full py-2 text-sm font-medium text-ink-muted hover:text-ink transition-colors disabled:opacity-50"
            >
              Not now
            </button>
            {/* Machine-scoped, and the reason is a real one: a passkey saved in
                a shared browser profile is offered to whoever sits down next,
                and would sign them in as its owner. */}
            <button
              onClick={handleSharedComputer}
              disabled={enrolling}
              className="w-full py-1 text-xs text-ink-muted hover:text-ink underline transition-colors disabled:opacity-50"
            >
              This is a shared computer — don't ask again
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
